import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './EditVideoPage.module.css';
import { AuthContext } from '../../context/authContext';
import apiRequest from '../../lib/apiRequest';
import logger from '../../lib/logger';

const EditVideoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [videoData, setVideoData] = useState({
    title: '',
    description: '',
    visibility: 'public',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');

  // Fetch current video details on mount
  useEffect(() => {
    const fetchVideoDetails = async () => {
      try {
        const res = await apiRequest(`/videos/selfieVideos/${id}`);
        if (res.data.success) {
          setVideoData({
            title: res.data.video.title || '',
            description: res.data.video.description || '',
            visibility: res.data.video.visibility || 'public',
            userId: res.data.video.user 
          });
        } else {
          setError(res.data.message || 'Failed to load video details.');
        }
      } catch (err) {
        logger.error('Error fetching video details:', err);
        setError('Could not load video details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchVideoDetails();
  }, [id]);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setVideoData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdateMessage('');
    setError('');
    
    try {
      const res = await apiRequest.patch(
        `/videos/selfieVideos/${id}`,
        videoData,
      );
      if (res.data.success) {
        setUpdateMessage('Video updated successfully!');
        // Redirect to the video detail page after a short delay
        setTimeout(() => {
          navigate(`/profile/${user._id}`);
        }, 1500);
      } else {
        setError(res.data.message || 'Failed to update video.');
      }
    } catch (err) {
        if (err.status == 429) {
            setError('You are making requests too quickly. Please wait a moment and try again.');
            return;
        }
      logger.error('Error updating video:', err);
      setError(err.response?.data?.message || 'Error updating video.');
    }
  };

  if (loading) {
    return <div className={styles.loadingMsg}>Loading video details...</div>;
  }

  if (error) {
    return <div className={styles.errorMsg}>{error}</div>;
  }

  // Optionally, ensure only the owner can edit (if user._id !== video's user, redirect)
  if (user && videoData && user._id !== videoData.userId) {
    logger.log(user._id, videoData.userId);
    // This check should ideally be done on the backend as well
    return <div className={styles.errorMsg}>You are not authorized to edit this video.</div>;
  }

  return (
    <div className={styles.editContainer}>
      <h2 className={styles.editTitle}>Edit Video</h2>
      {updateMessage && <div className={styles.successMessage}>{updateMessage}</div>}
      <form onSubmit={handleSubmit} className={styles.editForm}>
        <label htmlFor="title" className={styles.label}>Title</label>
        <input
          id="title"
          name="title"
          type="text"
          value={videoData.title}
          onChange={handleChange}
          className={styles.input}
          required
        />

        <label htmlFor="description" className={styles.label}>Description</label>
        <textarea
          id="description"
          name="description"
          value={videoData.description}
          onChange={handleChange}
          className={styles.textarea}
          rows="4"
        />

        <label htmlFor="visibility" className={styles.label}>Visibility</label>
        <select
          id="visibility"
          name="visibility"
          value={videoData.visibility}
          onChange={handleChange}
          className={styles.select}
        >
          <option value="public">Public</option>
          <option value="private">Private</option>
        </select>

        <button type="submit" className={styles.submitButton}>Update Video</button>
      </form>
    </div>
  );
};

export default EditVideoPage;
